// Itinerary Item Form (Travel Tab)
import React, { useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { Plane, Plus } from 'lucide-react';

interface ItineraryFormProps {
  onAdd: (item: { title: string; dueDate?: string; notes?: string; metadata?: any }) => void;
}

type ItineraryType = 'flight' | 'hotel' | 'activity' | 'restaurant' | 'other';

export default function ItineraryForm({ onAdd }: ItineraryFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [type, setType] = useState<ItineraryType>('flight');
  
  // Flight fields
  const [airline, setAirline] = useState('');
  const [flightNum, setFlightNum] = useState('');
  const [departDate, setDepartDate] = useState('');
  const [departTime, setDepartTime] = useState('');
  const [departAirport, setDepartAirport] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [arrivalAirport, setArrivalAirport] = useState('');
  
  // Hotel fields
  const [hotelName, setHotelName] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [address, setAddress] = useState('');
  const [nightlyRate, setNightlyRate] = useState('');
  
  // Activity fields
  const [activityName, setActivityName] = useState('');
  const [activityDate, setActivityDate] = useState('');
  const [activityTime, setActivityTime] = useState('');
  const [location, setLocation] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  
  // Shared
  const [confirmation, setConfirmation] = useState('');

  const resetForm = () => {
    setListName('');
    setAirline(''); setFlightNum(''); setDepartDate(''); setDepartTime(''); 
    setDepartAirport(''); setArrivalDate(''); setArrivalTime(''); setArrivalAirport('');
    setHotelName(''); setCheckIn(''); setCheckOut(''); setAddress(''); setNightlyRate('');
    setActivityName(''); setActivityDate(''); setActivityTime(''); setLocation(''); setCost('');
    setNotes(''); setConfirmation('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!listName.trim()) {
      alert('Please name your itinerary!');
      return;
    }
    
    let title = '';
    let itemDate = '';
    let itemNotes = '';
    let metadata: any = { type: 'itinerary', itineraryType: type, listName: listName.trim() };

    if (type === 'flight') {
      if (!airline || !flightNum) return;
      title = `✈️ ${airline} ${flightNum}`;
      itemDate = departDate ? `${departDate}T${departTime || '00:00'}:00.000Z` : '';
      itemNotes = `Depart: ${departAirport} → Arrive: ${arrivalAirport}${confirmation ? `\nConfirmation: ${confirmation}` : ''}`;
      metadata = { ...metadata, airline, flightNum, departDate, departTime, departAirport, arrivalDate, arrivalTime, arrivalAirport, confirmation };
    } else if (type === 'hotel') {
      if (!hotelName) return;
      title = `🏨 ${hotelName}`;
      itemDate = checkIn || '';
      itemNotes = `Check-in: ${checkIn}\nCheck-out: ${checkOut}${address ? `\nAddress: ${address}` : ''}${nightlyRate ? `\nRate: $${nightlyRate}/night` : ''}${confirmation ? `\nConfirmation: ${confirmation}` : ''}`;
      metadata = { ...metadata, hotelName, checkIn, checkOut, address, nightlyRate, confirmation };
    } else if (type === 'activity') {
      if (!activityName) return;
      title = `🎯 ${activityName}`;
      itemDate = activityDate ? `${activityDate}T${activityTime || '00:00'}:00.000Z` : '';
      itemNotes = `${location ? `Location: ${location}\n` : ''}${cost ? `Cost: $${cost}\n` : ''}${notes || ''}`;
      metadata = { ...metadata, activityName, activityDate, activityTime, location, cost, notes };
    } else if (type === 'restaurant') {
      if (!activityName) return;
      title = `🍽️ ${activityName}`;
      itemDate = activityDate ? `${activityDate}T${activityTime || '00:00'}:00.000Z` : '';
      itemNotes = `${location ? `Location: ${location}\n` : ''}${notes || ''}`;
      metadata = { ...metadata, activityName, activityDate, activityTime, location, notes };
    } else {
      if (!activityName) return;
      title = `📍 ${activityName}`;
      itemDate = activityDate ? `${activityDate}T${activityTime || '00:00'}:00.000Z` : '';
      itemNotes = notes || '';
      metadata = { ...metadata, activityName, activityDate, activityTime, notes };
    }

    onAdd({
      title,
      dueDate: itemDate || undefined,
      notes: itemNotes,
      metadata
    });

    resetForm();
    setIsOpen(false);
  };

  return (
    <>
      {/* Add Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="w-full md:w-auto px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation"
      >
        <Plane className="w-5 h-5" />
        Add Itinerary Item
      </button>

      {/* Form Modal */}
      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Itinerary Item"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Itinerary Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Itinerary Name *
            </label>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="e.g., Hawaii Trip, Business Travel, Europe Vacation"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base"
              required
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">Name this itinerary to organize your travel plans</p>
          </div>

          {/* Type Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Type *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {(['flight', 'hotel', 'activity', 'restaurant', 'other'] as ItineraryType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`py-3 px-4 rounded-lg font-semibold transition-colors touch-manipulation text-sm capitalize ${
                    type === t
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  {t === 'flight' && '✈️'} {t === 'hotel' && '🏨'} {t === 'activity' && '🎯'} {t === 'restaurant' && '🍽️'} {t === 'other' && '📍'} {t}
                </button>
              ))}
            </div>
          </div>

          {/* Dynamic Fields Based on Type */}
          {type === 'flight' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Airline *</label>
                  <input type="text" value={airline} onChange={(e) => setAirline(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="e.g., Delta, United" required autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Flight # *</label>
                  <input type="text" value={flightNum} onChange={(e) => setFlightNum(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="e.g., DL123" required />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Depart Date</label>
                  <input type="date" value={departDate} onChange={(e) => setDepartDate(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Time</label>
                  <input type="time" value={departTime} onChange={(e) => setDepartTime(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Airport</label>
                  <input type="text" value={departAirport} onChange={(e) => setDepartAirport(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="LAX" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Arrival Date</label>
                  <input type="date" value={arrivalDate} onChange={(e) => setArrivalDate(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Time</label>
                  <input type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Airport</label>
                  <input type="text" value={arrivalAirport} onChange={(e) => setArrivalAirport(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="JFK" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Confirmation #</label>
                <input type="text" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="ABC123" />
              </div>
            </>
          )}

          {type === 'hotel' && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Hotel Name *</label>
                <input type="text" value={hotelName} onChange={(e) => setHotelName(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="e.g., Marriott Downtown" required autoFocus />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Check-in</label>
                  <input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Check-out</label>
                  <input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Address</label>
                <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="123 Main St" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Nightly Rate ($)</label>
                  <input type="number" step="0.01" value={nightlyRate} onChange={(e) => setNightlyRate(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="150" inputMode="decimal" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Confirmation #</label>
                  <input type="text" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="ABC123" />
                </div>
              </div>
            </>
          )}

          {(type === 'activity' || type === 'restaurant' || type === 'other') && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{type === 'restaurant' ? 'Restaurant' : type === 'other' ? 'Item' : 'Activity'} Name *</label>
                <input type="text" value={activityName} onChange={(e) => setActivityName(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="e.g., Museum Tour, Dinner Reservation" required autoFocus />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Date</label>
                  <input type="date" value={activityDate} onChange={(e) => setActivityDate(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Time</label>
                  <input type="time" value={activityTime} onChange={(e) => setActivityTime(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Location</label>
                <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="Address or venue" />
              </div>
              {type === 'activity' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Cost ($)</label>
                  <input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base" placeholder="50" inputMode="decimal" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base resize-none" placeholder="Additional details..." />
              </div>
            </>
          )}

          {/* Submit Button */}
          <button type="submit" className="w-full py-4 px-6 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base mt-6">
            <Plus className="w-5 h-5" />
            Add to Itinerary
          </button>
        </form>
      </ResponsiveModal>
    </>
  );
}
